import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Shield, ShieldCheck, Upload, Download, AlertTriangle, Lock,
  Pencil, Palette, Monitor, LayoutList, LayoutGrid, Lightbulb,
  ArrowUp, Save, Check, FileText, Clock, XCircle, CheckCircle2
} from 'lucide-react';
import DashboardLayout from '../../layouts/DashboardLayout';
import Loading from '../../components/Loading/Loading';
import {
  getUserEnabledAppsApi, saveUserEnabledAppsApi, getUserDesktopIconsApi,
  saveUserDesktopIconsApi, updateKeyApi, getThemeStylesApi,
  getThemeWindowConfigApi, saveThemeWindowConfigApi,
  updateThemeApi, getUserConfigApi, updateThemeSettingApi, updateProtectionApi
} from '../../api/home';
import { uploadContractApi } from '../../api/auth';
import request from '../../utils/request';
import './RentalProtection.scss';

function RentalProtection() {
  // === State ===
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ visible: false, message: '' });

  // Step 1: Protection mode
  const [protectionMode, setProtectionMode] = useState('std'); // 'std' | 'adv'
  const [serverMode, setServerMode] = useState('normal'); // backend value: 'normal' | 'strong'
  const [contractStatus, setContractStatus] = useState(''); // 'pending' | 'approved' | 'rejected' | ''
  const [contractRejectReason, setContractRejectReason] = useState('');
  const [contractFile, setContractFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const contractInputRef = useRef(null);

  // Step 2: App config
  const [apps, setApps] = useState([]); // { platform_id, app_name, icon, color, user_key, enabled, mutex_group }
  const [mutexGroups, setMutexGroups] = useState([]); // [{ids: [id1, id2], names: [name1, name2]}]

  // Step 3: Promo method
  const [promoType, setPromoType] = useState('menu'); // 'menu' | 'desktop'
  // Menu sub-options
  const [menuName, setMenuName] = useState('租号菜单');
  const [menuIconPreview, setMenuIconPreview] = useState('');
  const [menuIconFile, setMenuIconFile] = useState(null);
  const menuIconRef = useRef(null);
  // Theme selection (using existing server theme list with previews)
  const [themes, setThemes] = useState([]);
  const [currentTheme, setCurrentTheme] = useState('');
  // Desktop icon states
  const [desktopStates, setDesktopStates] = useState({}); // { platform_id: true/false }

  // Saving states
  const [saving, setSaving] = useState(false);

  // Modal for editing key
  const [editModal, setEditModal] = useState({ visible: false, app: null, value: '' });
  const [editError, setEditError] = useState(false);

  // Download
  const [downloadInfo, setDownloadInfo] = useState(null);

  // === Computed ===
  const isLocked = protectionMode === 'adv' && contractStatus !== 'approved';
  const enabledApps = apps.filter(a => a.enabled);

  // === Toast ===
  const showToast = useCallback((message) => {
    setToast({ visible: true, message });
    setTimeout(() => setToast({ visible: false, message: '' }), 2800);
  }, []);

  // === Load Data ===
  const loadUserInfo = useCallback(async () => {
    try {
      const res = await request.get('/auth/userInfo');
      if (res.data) {
        const u = res.data;
        const mode = u.protection_mode || 'normal';
        setServerMode(mode);
        // If server says 'strong' AND contract approved => show adv selected
        if (mode === 'strong' && u.contract_status === 'approved') {
          setProtectionMode('adv');
        } else {
          setProtectionMode('std');
        }
        setContractStatus(u.contract_status || '');
        setContractRejectReason(u.contract_reject_reason || '');
        setCurrentTheme(u.cloud_theme || '');
      }
    } catch (e) {}
  }, []);

  const loadApps = useCallback(async () => {
    try {
      const res = await getUserEnabledAppsApi();
      const list = (res.data || []).map(a => ({
        platform_id: a.platform_id,
        app_name: a.app_name,
        icon: a.icon || '',
        color: a.color || '#3A3A3A',
        user_key: a.user_key || '',
        enabled: a.is_enabled !== undefined ? !!a.is_enabled : true,
        mutex_group: a.mutex_group || null
      }));
      setApps(list);
      // Build mutex groups
      const groupMap = {};
      list.forEach(app => {
        if (app.mutex_group) {
          if (!groupMap[app.mutex_group]) groupMap[app.mutex_group] = [];
          groupMap[app.mutex_group].push(app);
        }
      });
      setMutexGroups(Object.values(groupMap).filter(g => g.length > 1));
    } catch (e) {}
  }, []);

  const loadThemes = useCallback(async () => {
    try {
      const res = await getThemeStylesApi();
      setThemes(res.data || []);
    } catch (e) {}
  }, []);

  const loadWindowConfig = useCallback(async (themeCode) => {
    if (!themeCode) return;
    try {
      const res = await getThemeWindowConfigApi(themeCode);
      if (res.data) {
        setMenuName(res.data.wk_name || '租号菜单');
        setMenuIconPreview(res.data.desktop_ico || '');
      }
    } catch (e) {}
  }, []);

  const loadPromoConfig = useCallback(async () => {
    try {
      const res = await getUserConfigApi();
      if (res.data?.theme_setting) {
        const s = res.data.theme_setting;
        // trayMenu = menu mode, desktopIcon = desktop mode
        if (s.desktopIcon && !s.trayMenu) {
          setPromoType('desktop');
        } else {
          setPromoType('menu');
        }
      }
    } catch (e) {}
  }, []);

  const loadDesktopIcons = useCallback(async () => {
    try {
      const res = await getUserDesktopIconsApi();
      const iconIds = res.data || []; // array of platform_ids
      const ds = {};
      iconIds.forEach(id => { ds[id] = true; });
      setDesktopStates(ds);
    } catch (e) {}
  }, []);

  const loadClientDownload = useCallback(async () => {
    try {
      const res = await request.get('/home/clientDownload');
      if (res.data) setDownloadInfo(res.data);
    } catch (e) {}
  }, []);

  // Initial load
  useEffect(() => {
    setLoading(true);
    Promise.all([loadUserInfo(), loadApps(), loadDesktopIcons(), loadThemes(), loadPromoConfig(), loadClientDownload()])
      .finally(() => setLoading(false));
  }, [loadUserInfo, loadApps, loadDesktopIcons, loadThemes, loadPromoConfig, loadClientDownload]);

  // 页面聚焦时刷新数据
  // useEffect(() => {
  //   const onVisChange = () => {
  //     if (document.visibilityState !== 'visible') return;
  //     loadUserInfo();
  //     loadApps();
  //     loadDesktopIcons();
  //     loadThemes();
  //     loadPromoConfig();
  //   };
  //   document.addEventListener('visibilitychange', onVisChange);
  //   return () => document.removeEventListener('visibilitychange', onVisChange);
  // }, [loadUserInfo, loadApps, loadDesktopIcons, loadThemes, loadPromoConfig]);

  // When currentTheme changes, load window config
  useEffect(() => {
    if (currentTheme) {
      loadWindowConfig(currentTheme);
    }
  }, [currentTheme, loadWindowConfig]);

  // === Handlers ===

  // Step 1: Select protection mode (立即提交)
  const handleSelectMode = async (mode) => {
    setProtectionMode(mode);
    // 只有标准模式 或 高级模式+合同已通过 才提交到后端
    const targetMode = mode === 'adv' && contractStatus === 'approved' ? 'strong' : 'normal';
    if (targetMode !== serverMode) {
      try {
        await updateProtectionApi({ mode: targetMode });
        setServerMode(targetMode);
      } catch (e) {
        showToast(e.message || '切换失败');
      }
    }
  };

  // Contract upload
  const handleContractFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > 10 * 1024 * 1024) {
      showToast('文件大小不能超过10MB');
      return;
    }
    setContractFile(file);
  };

  const handleContractUpload = async () => {
    if (!contractFile || uploading) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', contractFile);
      const res = await uploadContractApi(fd);
      setContractStatus('pending');
      setContractFile(null);
      showToast('合同已上传，等待审核');
    } catch (e) {
      showToast(e.message || '上传失败');
    } finally {
      setUploading(false);
    }
  };

  // Step 2: Toggle app (立即提交变化到后端)
  const handleToggleApp = (platformId) => {
    setApps(prev => {
      const next = [...prev];
      const idx = next.findIndex(a => a.platform_id === platformId);
      if (idx === -1) return prev;
      const app = next[idx];
      const newEnabled = !app.enabled;

      // If enabling, check mutex
      const changedApps = [];
      if (newEnabled && app.mutex_group) {
        next.forEach((a, i) => {
          if (i !== idx && a.mutex_group === app.mutex_group && a.enabled) {
            next[i] = { ...a, enabled: false };
            changedApps.push({ platform_id: a.platform_id, is_enabled: false });
            showToast(`已关闭 ${a.app_name}（与 ${app.app_name} 互斥）`);
          }
        });
      }
      next[idx] = { ...app, enabled: newEnabled };
      changedApps.push({ platform_id: platformId, is_enabled: newEnabled });

      // Sync desktop states
      setDesktopStates(ds => {
        const newDs = { ...ds };
        changedApps.forEach(a => { if (!a.is_enabled) newDs[a.platform_id] = false; });
        return newDs;
      });

      // 只发送变化的应用，后端自己从当前主题同步到其它主题
      saveUserEnabledAppsApi({ apps: changedApps }).catch(e => {
        showToast(e.message || '保存失败');
      });

      return next;
    });
  };

  // Step 2: Edit key modal
  const handleOpenEditKey = (app) => {
    setEditModal({ visible: true, app, value: '' });
    setEditError(false);
  };
  const handleCloseEditKey = () => {
    setEditModal({ visible: false, app: null, value: '' });
    setEditError(false);
  };
  const handleSaveKey = async () => {
    const val = editModal.value.trim();
    if (!val) { setEditError(true); return; }
    try {
      await updateKeyApi({ platformId: editModal.app.platform_id, keyValue: val });
      setApps(prev => prev.map(a =>
        a.platform_id === editModal.app.platform_id ? { ...a, user_key: val } : a
      ));
      handleCloseEditKey();
      showToast('授权 Key 已更新');
    } catch (e) {
      showToast(e.message || '保存失败');
    }
  };

  // Step 3: Select promo type
  const handleSelectPromo = (type) => {
    setPromoType(type);
  };

  // Step 3: Theme select
  const handleSelectTheme = async (themeCode) => {
    if (themeCode === currentTheme) return;
    try {
      await updateThemeApi({ themeCode });
      setCurrentTheme(themeCode);
      showToast('主题已切换');
    } catch (e) {
      showToast(e.message || '切换失败');
    }
  };

  // Step 3: Desktop icon toggle
  const handleToggleDesktop = (platformId) => {
    setDesktopStates(prev => ({ ...prev, [platformId]: !prev[platformId] }));
  };

  // Menu icon upload
  const handleMenuIconChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    setMenuIconFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setMenuIconPreview(ev.target.result);
    reader.readAsDataURL(file);
    showToast('图标已上传');
  };

  // Save promo config (推广方式 + 主题/菜单设置 + 桌面图标选择)
  const handleSaveConfig = async () => {
    if (saving) return;
    setSaving(true);
    try {
      // 1. Save theme setting (promo type)
      const themeSetting = {
        trayMenu: promoType === 'menu',
        cloudDesktop: false,
        desktopIcon: promoType === 'desktop'
      };
      await updateThemeSettingApi(themeSetting);

      // 3. Save menu config (name + icon + theme)
      if (promoType === 'menu' && currentTheme) {
        const fd = new FormData();
        fd.append('themeCode', currentTheme);
        fd.append('wk_name', menuName.trim());
        if (menuIconFile) fd.append('icon_file', menuIconFile);
        await saveThemeWindowConfigApi(fd);
        setMenuIconFile(null);
      }

      // 4. Save desktop icons
      if (promoType === 'desktop') {
        const enabledDesktopIds = Object.entries(desktopStates)
          .filter(([id, v]) => v && apps.find(a => a.platform_id === Number(id) && a.enabled))
          .map(([id]) => Number(id));
        await saveUserDesktopIconsApi({ platformIds: enabledDesktopIds });
      }

      showToast('配置已保存');
    } catch (e) {
      showToast(e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  // Download client
  const handleDownloadClient = () => {
    if (!downloadInfo?.url) return;
    const token = localStorage.getItem('token');
    const isDev = process.env.NODE_ENV === 'development';
    const baseUrl = isDev ? 'http://localhost:8081' : '';
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = `${baseUrl}/umg/home/clientDownloadFile?token=${encodeURIComponent(token)}`;
    document.body.appendChild(iframe);
    setTimeout(() => document.body.removeChild(iframe), 5000);
  };

  // Template download
  const handleDownloadTemplate = async () => {
    try {
      const baseURL = process.env.NODE_ENV === 'development' ? '/umg' : 'https://xtray.96ck.com/umg';
      const res = await fetch(`${baseURL}/home/contractTemplate`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!res.ok) { showToast('下载失败'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = '合同模板.docx';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) { showToast('下载失败'); }
  };

  // === Render helpers ===
  const getMutexWarning = () => {
    if (mutexGroups.length === 0) return null;
    const names = mutexGroups.map(g => g.map(a => a.app_name).join(' 和 ')).join('；');
    return names + ' 不能同时启用';
  };

  // Step progress dots - 简单固定逻辑，与最终.html保持一致
  const getStepStatus = (step) => {
    if (isLocked) {
      // 高级模式未审核：只有step1完成
      return step === 1 ? 'done' : 'pending';
    }
    // 标准模式或高级已通过
    if (step === 1) return 'done';
    if (step === 2) return 'current';
    if (step === 3) return 'pending';
    return 'pending';
  };

  // 动态同步 step-line 高度与卡片高度
  const stepsCardsRef = useRef(null);
  const stepProgressRef = useRef(null);
  useEffect(() => {
    const sync = () => {
      if (!stepsCardsRef.current || !stepProgressRef.current) return;
      const wraps = stepsCardsRef.current.querySelectorAll('.rp-step-wrap');
      const lines = stepProgressRef.current.querySelectorAll('.rp-step-line');
      wraps.forEach((wrap, i) => {
        if (lines[i]) {
          lines[i].style.minHeight = Math.max(60, wrap.offsetHeight) + 'px';
        }
      });
    };
    sync();
    window.addEventListener('resize', sync);
    // 延迟再同步一次（等DOM渲染完）
    const timer = setTimeout(sync, 100);
    return () => { window.removeEventListener('resize', sync); clearTimeout(timer); };
  });

  if (loading) {
    return <DashboardLayout><Loading text="加载中..." /></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="rp-page">
        {/* Page header */}
        <div className="page-header">
          <div>
            <div className="page-title">租号保护</div>
            <div className="page-desc">仅需四步，提升你的租号收益。</div>
          </div>
          <button className="rp-btn" onClick={handleDownloadClient} disabled={!downloadInfo?.url}>
            <Download size={14} /> 下载客户端
          </button>
        </div>

        {/* Steps container */}
        <div className="rp-steps-container">
          {/* Progress sidebar */}
          <div className="rp-step-progress" ref={stepProgressRef}>
            <div className={`rp-step-dot ${getStepStatus(1)}`} />
            <div className="rp-step-line" />
            <div className={`rp-step-dot ${getStepStatus(2)}`} />
            <div className="rp-step-line" />
            <div className={`rp-step-dot ${getStepStatus(3)}`} />
            <div className="rp-step-line" />
            <div className={`rp-step-dot ${getStepStatus(4)}`} />
          </div>

          {/* Cards */}
          <div className="rp-steps-cards" ref={stepsCardsRef}>

            {/* Step 1: 选择保护模式 */}
            <div className="rp-step-wrap">
              <div className="rp-card">
                <div className="rp-step-header">
                  <div className="rp-step-badge">1</div>
                  <div className="rp-step-title">选择保护模式</div>
                  <div className="rp-step-desc">决定保护范围</div>
                </div>
                <div className="rp-mode-wrap">
                  <div
                    className={`rp-mode-card ${protectionMode === 'std' ? 'sel' : ''}`}
                    onClick={() => handleSelectMode('std')}
                  >
                    <span className="rp-mode-badge badge-std">推荐</span>
                    <div className="rp-mode-top">
                      <div className={`rp-mode-radio ${protectionMode === 'std' ? 'checked' : ''}`} />
                      <Shield size={22} className="rp-mode-icon" />
                      <span className="rp-mode-name">标准模式</span>
                    </div>
                    <div className="rp-mode-desc">
                      只保护我们目录里面的租号，<strong>属于你</strong>。无需额外审核，立即生效。
                    </div>
                  </div>
                  <div
                    className={`rp-mode-card ${protectionMode === 'adv' ? 'sel' : ''}`}
                    onClick={() => handleSelectMode('adv')}
                  >
                    <span className="rp-mode-badge badge-adv">高级</span>
                    <div className="rp-mode-top">
                      <div className={`rp-mode-radio ${protectionMode === 'adv' ? 'checked' : ''}`} />
                      <ShieldCheck size={22} className="rp-mode-icon" />
                      <span className="rp-mode-name">高级模式</span>
                    </div>
                    <div className="rp-mode-desc">
                      保护这台机器上的租号收益，<strong>属于你</strong>。需上传合同，审核通过后方可开启。
                    </div>
                  </div>
                </div>

                {/* Advanced section: contract */}
                {protectionMode === 'adv' && (
                  <div className="rp-adv-section">
                    <div className="rp-sec-label"><FileText size={15} /> 合同上传与审核</div>

                    {/* Audit status */}
                    {contractStatus === 'pending' && (
                      <div className="rp-adv-status pending">
                        <Clock size={18} />
                        <div>
                          <div className="rp-adv-status-title">待审核</div>
                          <div className="rp-adv-status-desc">合同已上传，我们将在 1-3 个工作日内完成审核。</div>
                        </div>
                      </div>
                    )}
                    {contractStatus === 'approved' && (
                      <div className="rp-adv-status approved">
                        <CheckCircle2 size={18} />
                        <div>
                          <div className="rp-adv-status-title">审核通过</div>
                          <div className="rp-adv-status-desc">高级模式已解锁，你的租号收益保护已全面生效。</div>
                        </div>
                      </div>
                    )}
                    {contractStatus === 'rejected' && (
                      <div className="rp-adv-status rejected">
                        <XCircle size={18} />
                        <div>
                          <div className="rp-adv-status-title">审核拒绝</div>
                          <div className="rp-adv-status-desc">
                            你的合同未通过审核{contractRejectReason ? `：${contractRejectReason}` : ''}，请重新上传或联系客服处理。
                          </div>
                        </div>
                      </div>
                    )}
                    {!contractStatus && (
                      <div className="rp-adv-status pending">
                        <Clock size={18} />
                        <div>
                          <div className="rp-adv-status-title">待上传</div>
                          <div className="rp-adv-status-desc">请上传合同文件，我们将在 1-3 个工作日内完成审核。</div>
                        </div>
                      </div>
                    )}

                    {/* Upload area */}
                    {contractStatus !== 'approved' && (
                      <div>
                        <div
                          className={`rp-upload-area ${contractFile ? 'has-file' : ''}`}
                          onClick={() => contractInputRef.current?.click()}
                        >
                          <Upload size={32} />
                          <p>{contractFile ? contractFile.name : '点击上传合同文件'}</p>
                          <span>{contractFile ? '点击重新选择' : '支持 PDF、JPG、PNG，文件大小不超过 10MB'}</span>
                        </div>
                        <input
                          ref={contractInputRef}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          style={{ display: 'none' }}
                          onChange={handleContractFileChange}
                        />
                        {contractFile && (
                          <div className="rp-template-row">
                            <button className="rp-btn rp-btn-primary rp-btn-sm" onClick={handleContractUpload} disabled={uploading}>
                              {uploading ? '上传中...' : '确认上传'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Template download */}
                    <div className="rp-template-row">
                      <button className="rp-btn rp-btn-sm" onClick={handleDownloadTemplate}>
                        <Download size={14} /> 下载合同模板
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Step 2: 租号软件配置 */}
            <div className="rp-step-wrap">
              <div className="rp-card" style={{ position: 'relative' }}>
                {isLocked && (
                  <div className="rp-lock-mask">
                    <Lock size={28} />
                    <p>请先完成高级模式审核后方可使用</p>
                  </div>
                )}
                <div className="rp-step-header">
                  <div className="rp-step-badge">2</div>
                  <div className="rp-step-title">租号软件配置</div>
                  <div className="rp-step-desc">选择你要保护的租号软件</div>
                </div>
                {getMutexWarning() && (
                  <div className="rp-mutex-warning">
                    <AlertTriangle size={14} /> {getMutexWarning()}
                  </div>
                )}
                <table className="rp-key-table">
                  <thead>
                    <tr>
                      <th>应用</th>
                      <th>当前授权 Key</th>
                      <th style={{ width: 80, textAlign: 'center' }}>启用</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apps.map(app => (
                      <tr key={app.platform_id}>
                        <td>
                          <div className="rp-app-name">
                            {app.icon ? (
                              <img src={app.icon} alt="" className="rp-app-icon" />
                            ) : (
                              <span className="rp-app-avatar" style={{ background: app.color }}>
                                {app.app_name.charAt(0)}
                              </span>
                            )}
                            {app.app_name}
                          </div>
                        </td>
                        <td>
                          <div className="rp-key-val">{app.user_key || '—'}</div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <label className="rp-toggle">
                            <input
                              type="checkbox"
                              checked={app.enabled}
                              onChange={() => handleToggleApp(app.platform_id)}
                            />
                            <span className="rp-track" />
                          </label>
                        </td>
                        <td>
                          <button className="rp-btn rp-btn-sm" onClick={() => handleOpenEditKey(app)}>
                            修改 Key
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Step 3: 选择推广方式 */}
            <div className="rp-step-wrap">
              <div className="rp-card" style={{ position: 'relative' }}>
                {isLocked && (
                  <div className="rp-lock-mask">
                    <Lock size={28} />
                    <p>请先完成高级模式审核后方可使用</p>
                  </div>
                )}
                <div className="rp-step-header">
                  <div className="rp-step-badge">3</div>
                  <div className="rp-step-title">选择推广方式</div>
                  <div className="rp-step-desc">决定用户看到的桌面形式</div>
                </div>
                <div className="rp-promo-wrap">
                  <div
                    className={`rp-promo-card ${promoType === 'menu' ? 'sel' : ''}`}
                    onClick={() => handleSelectPromo('menu')}
                  >
                    <div className="rp-promo-top">
                      <div className={`rp-promo-radio ${promoType === 'menu' ? 'checked' : ''}`} />
                      <LayoutList size={22} className="rp-promo-icon" />
                      <span className="rp-promo-name">采用租号菜单</span>
                    </div>
                    <div className="rp-promo-desc">
                      以弹窗菜单形式展示所有租号应用，用户点击图标后选择软件启动。
                    </div>
                  </div>
                  <div
                    className={`rp-promo-card ${promoType === 'desktop' ? 'sel' : ''}`}
                    onClick={() => handleSelectPromo('desktop')}
                  >
                    <div className="rp-promo-top">
                      <div className={`rp-promo-radio ${promoType === 'desktop' ? 'checked' : ''}`} />
                      <LayoutGrid size={22} className="rp-promo-icon" />
                      <span className="rp-promo-name">采用桌面图标</span>
                    </div>
                    <div className="rp-promo-desc">
                      直接在桌面放置各软件快捷图标，用户双击对应图标启动。
                    </div>
                    <div className="rp-promo-tip">
                      <Lightbulb size={14} />
                      <span>我们建议你只放一个桌面图标，我们会保护你的所有租号软件收益。</span>
                    </div>
                  </div>
                </div>

                {/* Menu sub-section */}
                {promoType === 'menu' && (
                  <div className="rp-exp-section">
                    <div className="rp-section-divider">
                      <div className="rp-sec-label"><Pencil size={15} /> 菜单名称 / 菜单图标</div>
                      <div className="rp-sec-sub">请设置桌面图标的名称和图标，用户将看到这些内容。</div>
                      <div className="rp-field-row">
                        <div>
                          <label className="rp-field-label">菜单名称</label>
                          <input
                            className="rp-field-input"
                            type="text"
                            value={menuName}
                            onChange={e => setMenuName(e.target.value)}
                          />
                          <div className="rp-field-hint">默认：租号菜单</div>
                        </div>
                        <div>
                          <label className="rp-field-label">菜单图标</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div className="rp-icon-preview-box">
                              {menuIconPreview ? (
                                <img src={menuIconPreview} alt="" />
                              ) : '🎮'}
                            </div>
                            <div className="rp-icon-upload" onClick={() => menuIconRef.current?.click()}>
                              <Upload size={20} />
                              <span>上传图标</span>
                            </div>
                            <input
                              ref={menuIconRef}
                              type="file"
                              accept=".ico,.png"
                              style={{ display: 'none' }}
                              onChange={handleMenuIconChange}
                            />
                          </div>
                          <div className="rp-field-hint">默认图标已预设，支持 PNG / ICO</div>
                        </div>
                      </div>
                    </div>
                    <div className="rp-sec-label"><Palette size={15} /> 菜单主题</div>
                    <div className="rp-sec-sub">选择租号菜单的视觉风格，菜单内容为上方勾选的应用。</div>
                    {/* Theme grid - using existing server theme list with preview images */}
                    {themes.length === 0 ? (
                      <p style={{ color: '#9ca3af', fontSize: 13 }}>暂无可用主题</p>
                    ) : (
                      <div className="cloud-desktop-grid">
                        {themes.filter(t => Number(t.is_fixed) === 0).map(theme => (
                          <div
                            key={theme.code}
                            className={`cloud-desktop-card ${currentTheme === theme.code ? 'active' : ''}`}
                            onClick={() => handleSelectTheme(theme.code)}
                          >
                            {theme.preview_img ? (
                              <img src={theme.preview_img} alt={theme.name} className="cloud-desktop-card-img" />
                            ) : (
                              <div className="cloud-desktop-card-placeholder">
                                <Monitor size={28} />
                              </div>
                            )}
                            <div className="cloud-desktop-card-info">
                              <span className="cloud-desktop-card-name">{theme.name}</span>
                              {currentTheme === theme.code && (
                                <span className="cloud-desktop-card-badge"><Check size={12} /> 当前</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Desktop icon sub-section */}
                {promoType === 'desktop' && (
                  <div className="rp-exp-section">
                    <div className="rp-sec-label"><Monitor size={15} /> 选择桌面图标</div>
                    <div className="rp-sec-sub">选择你要在客户端桌面放置的桌标，开启的应用图标将出现在用户桌面。</div>
                    <div>
                      {apps.map(app => {
                        const enabled = app.enabled;
                        const desktopOn = desktopStates[app.platform_id];
                        return (
                          <div key={app.platform_id} className={`rp-desktop-icon-item ${!enabled ? 'disabled' : ''}`}>
                            <div className="rp-desktop-icon-img" style={{ background: enabled ? app.color : '#ccc' }}>
                              {app.icon ? (
                                <img src={app.icon} alt="" style={{ width: '100%', height: '100%', borderRadius: 9, objectFit: 'cover' }} />
                              ) : app.app_name.charAt(0)}
                            </div>
                            <div className="rp-desktop-icon-info">
                              <div className="rp-desktop-icon-name">{app.app_name}</div>
                              {enabled ? (
                                <div className="rp-desktop-icon-sub">
                                  {desktopOn ? '将在桌面显示' : '不在桌面显示'}
                                </div>
                              ) : (
                                <div className="rp-desktop-icon-hint">
                                  <ArrowUp size={12} /> 请先在「租号软件配置」中开启此应用
                                </div>
                              )}
                            </div>
                            <label className="rp-toggle" style={!enabled ? { pointerEvents: 'none' } : {}}>
                              <input
                                type="checkbox"
                                checked={enabled && desktopOn}
                                disabled={!enabled}
                                onChange={() => handleToggleDesktop(app.platform_id)}
                              />
                              <span className="rp-track" />
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="rp-save-bar">
                  <button className="rp-btn rp-btn-primary" onClick={handleSaveConfig} disabled={saving}>
                    <Save size={14} /> {saving ? '保存中...' : '保存更改'}
                  </button>
                </div>
              </div>
            </div>

            {/* Step 4: 下载客户端 */}
            <div className="rp-step-wrap">
              <div className="rp-card" style={{ position: 'relative' }}>
                {isLocked && (
                  <div className="rp-lock-mask">
                    <Lock size={28} />
                    <p>请先完成高级模式审核后方可使用</p>
                  </div>
                )}
                <div className="rp-step-header">
                  <div className="rp-step-badge">4</div>
                  <div className="rp-step-title">下载并安装客户端</div>
                  <div className="rp-step-desc">完成配置后部署到机器</div>
                </div>
                <div className="rp-download-section">
                  <div>
                    <div className="rp-download-title">客户端安装包</div>
                    <div className="rp-download-desc">确保所有配置无误后保存，然后下载客户端下发开机启动即可开始提升租号收益。</div>
                  </div>
                  <button className="rp-btn rp-btn-primary" style={{ flexShrink: 0 }} onClick={handleDownloadClient} disabled={!downloadInfo?.url}>
                    <Download size={14} /> 下载客户端
                  </button>
                </div>
              </div>
            </div>

          </div>{/* end steps-cards */}
        </div>{/* end steps-container */}
      </div>{/* end rp-page */}

      {/* Edit Key Modal */}
      {editModal.visible && (
        <div className="rp-modal-overlay" onClick={handleCloseEditKey}>
          <div className="rp-modal" onClick={e => e.stopPropagation()}>
            <div className="rp-modal-title">
              修改 {editModal.app?.app_name} 的授权 Key
            </div>
            <div className="rp-modal-sub">
              当前值：{(editModal.app?.user_key || '').substring(0, 32)}{(editModal.app?.user_key || '').length > 32 ? '…' : ''}
            </div>
            <input
              className={`rp-modal-input ${editError ? 'error' : ''}`}
              type="text"
              placeholder="粘贴或输入授权 Key"
              value={editModal.value}
              onChange={e => { setEditModal(prev => ({ ...prev, value: e.target.value })); setEditError(false); }}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveKey(); }}
              autoFocus
            />
            <div className="rp-modal-actions">
              <button className="rp-btn" onClick={handleCloseEditKey}>取消</button>
              <button className="rp-btn rp-btn-primary" onClick={handleSaveKey}>
                <Check size={14} /> 保存
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast - bottom right style */}
      {toast.visible && (
        <div className="rp-toast">
          <Check size={16} />
          <span>{toast.message}</span>
        </div>
      )}
    </DashboardLayout>
  );
}

export default RentalProtection;
